import React, {useCallback, useContext} from 'react';
import {View} from 'react-native';
import {makeStyles} from '@rneui/themed';
import {FlashList} from '@shopify/flash-list';

import {ScreenTypes, useWebScreenDimensions} from '../../hooks/useWebScreenDimensions';
import {OrderLine} from '../../transport/apiServiceResponse';
import ItemSeparator from '../ItemSeparator';
import LoadingImg from '../LoadingImg';
import {ProductTileCarousel} from '../ProductTile';
import Text from '../themed/Text';
import ContentView from '../ui-controls/layout/ContentView';

type ProductGridProps = {
  items: OrderLine[];
  isLoading?: boolean;
};

const Context = React.createContext({isLoading: false});

const ProductGrid = ({items, isLoading}: ProductGridProps) => {
  const {isWebDesktopScreen, screenType} = useWebScreenDimensions();

  let numColumns = 2;

  switch (screenType) {
    case ScreenTypes.mobile:
      numColumns = 2;
      break;
    case ScreenTypes.desktop:
      numColumns = 4;
      break;
    case ScreenTypes.desktopLarge:
      numColumns = 6;
      break;
  }

  const styles = useStyles();

  const renderItem = useCallback(
    ({item}: {item: OrderLine}) => (
      <View style={styles.tileWrapper}>
        <ProductTileCarousel item={item} fullWidth testContext="grid" />
      </View>
    ),
    [styles.tileWrapper],
  );

  return (
    <Context.Provider value={{isLoading: !!isLoading}}>
      <ContentView style={{flex: 1}} parentStyle={{flex: 1}}>
        {isLoading ? (
          <View
            style={{
              justifyContent: 'center',
              alignItems: 'center',
              flexGrow: 1,
            }}>
            <LoadingImg />
          </View>
        ) : (
          <FlashList
            key={`product-flashlist-${numColumns}`}
            testID="product-flatlist"
            data={items}
            renderItem={renderItem}
            keyExtractor={item => String(item.id)}
            numColumns={numColumns}
            estimatedItemSize={280}
            showsVerticalScrollIndicator={isWebDesktopScreen}
            initialNumToRender={isWebDesktopScreen ? 25 : 15}
            ItemSeparatorComponent={ItemSeparator}
            ListEmptyComponent={<EmptyComponent />}
            contentContainerStyle={styles.flashListContent}
          />
        )}
      </ContentView>
    </Context.Provider>
  );
};

const EmptyComponent = () => {
  const {isLoading} = useContext(Context);
  return isLoading ? (
    <LoadingImg />
  ) : (
    <View
      style={{
        justifyContent: 'flex-end',
        alignItems: 'center',
        height: 200,
      }}>
      <Text variant="body2">NO PRODUCTS FOUND</Text>
    </View>
  );
};

const useStyles = makeStyles(() => {
  return {
    tileWrapper: {
      flex: 1,
      paddingHorizontal: 4,
    },
    flashListContent: {
      paddingHorizontal: 4,
    },
  };
});

export default ProductGrid;
